import React from "react";
import { Check, AlertTriangle, AlertOctagon, HelpCircle, Pause, Circle, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

// Distinctive RequiSure Readiness Rail: ORDER → EVIDENCE → COVERAGE → ACTION → RELEASE → BILLING
// State per stage: complete | action | review | unable | held | future
const ICON = {
  complete: { I: Check, cls: "bg-emerald-500 text-white", ring: "ring-emerald-200" },
  action: { I: AlertTriangle, cls: "bg-amber-500 text-white", ring: "ring-amber-200" },
  review: { I: AlertOctagon, cls: "bg-rose-500 text-white", ring: "ring-rose-200" },
  unable: { I: HelpCircle, cls: "bg-indigo-500 text-white", ring: "ring-indigo-200" },
  held: { I: Pause, cls: "bg-slate-500 text-white", ring: "ring-slate-200" },
  future: { I: Circle, cls: "bg-white text-slate-300 border border-slate-300", ring: "ring-slate-100" },
};

function stateForColor(color) {
  return color === "GREEN" ? "complete" : color === "YELLOW" ? "action"
    : color === "UNABLE" ? "unable" : "review";
}

export function deriveRailStates(order) {
  const ev = order?.latest_evaluation;
  const color = order?.readiness_color;
  const admin = !(ev?.missing_admin_fields?.length);
  const evidenceState = stateForColor(color);
  const coverageState = ev?.unsupported ? "unable" : stateForColor(color);
  const actionState = color === "GREEN" ? "complete" : color === "UNABLE" ? "unable" : "action";
  const releaseState = order?.released_simulated ? "complete" : (color === "GREEN" ? "action" : "held");
  return [
    { key: "ORDER", state: admin ? "complete" : "action" },
    { key: "EVIDENCE", state: evidenceState },
    { key: "COVERAGE", state: coverageState },
    { key: "ACTION", state: actionState },
    { key: "RELEASE", state: releaseState },
    { key: "BILLING", state: "future" },
  ];
}

export function ReadinessRail({ order, className }) {
  const stages = deriveRailStates(order);
  return (
    <div className={cn("flex items-stretch gap-1 overflow-x-auto rounded-md border border-slate-200 bg-white p-3", className)}
      data-testid="readiness-rail">
      {stages.map((s, i) => {
        const cfg = ICON[s.state] || ICON.future;
        const { I } = cfg;
        return (
          <React.Fragment key={s.key}>
            <div className="flex min-w-[92px] flex-1 flex-col items-center gap-1.5">
              <div className={cn("flex h-8 w-8 items-center justify-center rounded-md ring-4", cfg.cls, cfg.ring)}>
                <I className="h-4 w-4" />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">{s.key}</span>
              <span className="text-[9px] uppercase tracking-wide text-slate-400">
                {s.state === "future" ? "Future" : s.state === "held" ? "Held" : s.state === "unable" ? "Unable" : s.state === "review" ? "Review" : s.state === "action" ? "Action" : "Complete"}
              </span>
            </div>
            {i < stages.length - 1 && (
              <div className="flex items-center pt-1"><ChevronRight className="h-4 w-4 text-slate-300" /></div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default ReadinessRail;
